import React, { useEffect, useState } from "react";
import { sdh_fieldValues, type FieldValues } from "../api/sdhAPI";

/**
 * "Where do this field's values come from?" — the answer the reviewer could not previously get.
 * Shows what the data actually contains, what the pipeline inferred, what the client's own reference
 * extract supplies, and how the physical table is delivered. Three MATKL rules were rejected as
 * "no values in the raw data" when the field was in fact 100% populated and fully governed; this panel
 * exists so that question is answerable in one click.
 */
export default function FieldValuesPanel(
  { entityId, table, column, onClose }:
  { entityId: string; table: string; column: string; onClose: () => void }
) {
  const [d, setD] = useState<FieldValues | null>(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setBusy(true); setErr(null);
      try { const r = await sdh_fieldValues(entityId, table, column); if (live) setD(r); }
      catch (e: any) { if (live) setErr(e?.response?.data?.detail || e?.message || "Could not load values"); }
      finally { if (live) setBusy(false); }
    })();
    return () => { live = false; };
  }, [entityId, table, column]);

  const ob = d?.observed, s1 = d?.step1_profiled, s2 = d?.step2_client_supplied;
  const governed = s2?.values || [];
  const govSet = new Set(governed.map((g) => String(g.code).trim().toUpperCase()));

  return (
    <div className="fv-scrim" onClick={onClose}>
      <div className="fv" onClick={(e) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header className="fv-h">
          <div>
            <div className="eyebrow">FIELD VALUES</div>
            <div className="title">{table}.{column}</div>
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>

        <div className="fv-body">
          {busy && <div className="mut">Loading…</div>}
          {err && <div className="fv-err">{err}</div>}

          {d && (
            <>
              {d.grain && (
                <div className="grain">
                  <b>How this table is delivered:</b> {d.grain.grain}
                  {d.grain.note && <div className="n">{d.grain.note}</div>}
                </div>
              )}

              {(d.reconciliation || []).length > 0 && (
                <ul className="recon">
                  {d.reconciliation!.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              )}

              <div className="cols">
                {/* what the data really has */}
                <section>
                  <div className="sh">In your data
                    {ob && <span className="c">{ob.distinct_count} distinct · {ob.null_pct}% empty</span>}
                  </div>
                  {!ob ? <div className="mut">Not profiled.</div> : (
                    <div className="vals">
                      {(ob.values || []).map((v) => {
                        const known = govSet.size === 0 || govSet.has(String(v.value).trim().toUpperCase());
                        return (
                          <div className={"v" + (known ? "" : " off")} key={v.value}>
                            <span className="k">{v.value === "" ? "(blank)" : v.value}</span>
                            <span className="n">{v.rows.toLocaleString()}</span>
                            {v.pct != null && <span className="p">{v.pct}%</span>}
                            {!known && <span className="warn" title="Not in the governed list">!</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* what the client supplied */}
                <section>
                  <div className="sh">From your reference data
                    {s2 && <span className="c">{governed.length} code(s)</span>}
                  </div>
                  {s2 && s2.has_status_information === false && (
                    <div className="nostatus">
                      <b>No value status supplied.</b> This reference has no Active/Obsolete column, so a
                      retired code cannot be told apart from a valid one — and rules will treat every code
                      as enforceable.
                      <div className="n">Re-upload the reference including its status column on the
                        Reference Data page.</div>
                    </div>
                  )}
                  {!s2 ? <div className="mut">This column is not mapped in the reference extract.</div>
                    : governed.length === 0 ? (
                      <div className="gap">
                        Mapped to <b>{s2.mapped_logical_field}</b>, but no governed codes are available
                        {s2.domain_present_in_client_extract === false && " — that domain was not in the file you supplied"}.
                        <div className="n">Load it on the Reference Data page and value rules will bind to it.</div>
                      </div>
                    ) : (
                      <div className="vals">
                        {governed.map((g) => {
                          const dead = g.status && g.status !== "ACTIVE";
                          return (
                            <div className={"v" + (dead ? " dead" : "")} key={g.code}>
                              <span className="k">{g.code}</span>
                              {g.label && <span className="l">{g.label}</span>}
                              {dead && <span className="st" title="Retired in your reference data — should not be enforced as valid">{g.status}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  {s1 && (
                    <div className="s1">
                      <b>Pipeline-inferred list:</b> {(s1.values || []).length} value(s)
                      {s1.status ? ` · ${s1.status}` : ""}
                      {s1.reviewed_by ? ` by ${s1.reviewed_by}` : ""}
                      {s1.reviewed_at ? ` on ${String(s1.reviewed_at).slice(0, 10)}` : ""}
                      {s1.rationale && <div className="n">{s1.rationale}</div>}
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.fv-scrim{position:fixed;inset:0;z-index:92;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.fv{--line:#e3e8ee;--muted:#6b7c8d;width:min(960px,96vw);max-height:90vh;background:#fff;border-radius:16px;
 display:flex;flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);color:#0E2436}
.fv-h{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#0e2436;color:#fff}
.fv-h .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.fv-h .title{font-size:16px;font-weight:700;font-family:ui-monospace,monospace}
.fv-h .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.fv-h .x:hover{color:#fff}
.fv-body{overflow:auto;padding:16px 20px 20px}
.mut{color:var(--muted);font-size:13px}
.fv-err{background:#fdeceb;color:#b23a30;border-radius:8px;padding:9px 12px;font-size:12.5px}
.grain{background:#f6f8fa;border-left:3px solid #43607e;border-radius:8px;padding:9px 12px;font-size:12.5px;margin-bottom:10px}
.grain .n{color:var(--muted);margin-top:4px;line-height:1.5}
.recon{margin:0 0 14px;padding-left:18px;font-size:13px;color:#33404e;line-height:1.6}
.cols{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.cols .sh{display:flex;align-items:baseline;gap:8px;font-size:11px;letter-spacing:.07em;text-transform:uppercase;
 color:#9aa6b2;font-weight:700;margin-bottom:7px}
.cols .sh .c{font-size:11px;color:var(--muted);text-transform:none;letter-spacing:0;font-weight:400}
.vals{border:1px solid var(--line);border-radius:9px;max-height:320px;overflow:auto}
.vals .v{display:flex;align-items:center;gap:9px;padding:5px 10px;border-bottom:1px solid #eef1f4;font-size:12.5px}
.vals .v:last-child{border-bottom:0}
.vals .v.off{background:#fdf6f5}
.vals .k{font-family:ui-monospace,monospace;font-weight:600}
.vals .l{color:var(--muted);font-size:11.5px}
.vals .n{margin-left:auto;font-family:ui-monospace,monospace;color:#42586e}
.vals .p{font-size:11px;color:var(--muted);width:44px;text-align:right}
.vals .warn{color:#b23a30;font-weight:700}
.nostatus{background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:10px 12px;font-size:12.5px;
 color:#8a6516;line-height:1.5;margin-bottom:9px}
.nostatus .n{margin-top:5px;color:#9a7b3a}
.vals .v.dead{background:#fdf6f5;opacity:.85}
.vals .v.dead .k{text-decoration:line-through}
.vals .st{margin-left:auto;font-size:10px;font-weight:700;letter-spacing:.05em;color:#b23a30;background:#fbe9e7;border-radius:20px;padding:1px 8px}
.gap{background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:10px 12px;font-size:12.5px;color:#8a6516;line-height:1.5}
.gap .n{margin-top:5px;color:#9a7b3a}
.s1{margin-top:10px;font-size:12px;color:#4a5763;background:#f8fafb;border-radius:8px;padding:8px 11px;line-height:1.5}
.s1 .n{color:var(--muted);margin-top:4px}
@media(max-width:820px){.cols{grid-template-columns:1fr}}
`;
